import { alarmRingsAt, formatTime, timerIdle } from '@obv/core';
import { useEffect, useRef } from 'react';
import { startChime, unlockAudio } from '@/lib/chime';
import { useAlarms, useDismissAlarm } from '@/queries/alarms';
import { newMutationKey } from '@/queries/hooks';
import { useClockTools } from './clockStore';
import { useDisplay } from './context';

/** A ring nobody answers stops on its own, so an empty house isn't chiming all day. */
const AUTO_STOP_MS = 10 * 60_000;
/** A timer that ran out while the app was closed still rings if it ended this recently. */
const LATE_TIMER_MS = 10 * 60_000;

/** Lets iOS play the chime later: audio must first be started from a user gesture. */
export function useAudioUnlock() {
  useEffect(() => {
    const onTouch = () => {
      if (unlockAudio()) {
        document.removeEventListener('touchend', onTouch);
        document.removeEventListener('click', onTouch);
      }
    };
    document.addEventListener('touchend', onTouch);
    document.addEventListener('click', onTouch);
    return () => {
      document.removeEventListener('touchend', onTouch);
      document.removeEventListener('click', onTouch);
    };
  }, []);
}

/** Watches alarms, snoozes and the timer from any screen, and shows the ring over everything. */
export function Ringer() {
  const ctx = useDisplay();
  const alarms = useAlarms();
  const dismiss = useDismissAlarm();
  const ringing = useClockTools((s) => s.ringing);
  const snoozeAt = useClockTools((s) => (s.snooze ? s.snooze.at : null));
  const timerEndsAt = useClockTools((s) => s.timer.endsAt);
  const { dateKey, hhmm } = ctx.clock;

  // Alarms: checked on every minute tick.
  useEffect(() => {
    const st = useClockTools.getState();
    if (!alarms.data || st.ringing) return;
    for (const a of alarms.data) {
      if (!alarmRingsAt(a, dateKey, hhmm)) continue;
      const key = `${a.id}@${dateKey} ${hhmm}`;
      if (st.lastFired === key) return;
      st.ring({ kind: 'alarm', alarmId: a.id, label: a.label || 'Alarm', time: a.time, oneTime: a.days.length === 0 }, key);
      return;
    }
  }, [alarms.data, dateKey, hhmm]);

  // Snooze: one timeout for the pending snooze.
  useEffect(() => {
    if (snoozeAt === null) return;
    const fire = () => {
      const st = useClockTools.getState();
      const sn = st.snooze;
      if (!sn) return;
      useClockTools.setState({ snooze: null });
      if (!st.ringing) st.ring({ kind: 'alarm', alarmId: sn.alarmId, label: sn.label, time: sn.time, oneTime: sn.oneTime });
    };
    const t = setTimeout(fire, Math.max(0, snoozeAt - Date.now()));
    return () => clearTimeout(t);
  }, [snoozeAt]);

  // Timer: one timeout for its end.
  useEffect(() => {
    if (timerEndsAt === null) return;
    const fire = () => {
      const st = useClockTools.getState();
      if (st.timer.endsAt === null) return;
      if (Date.now() - st.timer.endsAt > LATE_TIMER_MS) {
        useClockTools.setState({ timer: timerIdle(st.timer.durationMs) });
        return;
      }
      if (st.ringing) return;
      st.ring({ kind: 'timer' });
    };
    const t = setTimeout(fire, Math.max(0, timerEndsAt - Date.now()));
    return () => clearTimeout(t);
  }, [timerEndsAt]);

  if (!ringing) return null;
  const stop = () => {
    if (ringing.kind === 'alarm' && ringing.oneTime) dismiss.mutate({ id: ringing.alarmId, key: newMutationKey() });
    useClockTools.getState().stopRinging();
  };
  return <RingOverlay title={ringing.kind === 'alarm' ? formatTime(ringing.time, ctx.prefs.h24) : 'Time’s up'} sub={ringing.kind === 'alarm' ? ringing.label : 'Timer'} snoozable={ringing.kind === 'alarm'} onStop={stop} />;
}

function RingOverlay({ title, sub, snoozable, onStop }: { title: string; sub: string; snoozable: boolean; onStop: () => void }) {
  const snoozeRinging = useClockTools((s) => s.snoozeRinging);
  const extend = useClockTools((s) => s.extendFinishedTimer);
  // The parent re-renders every minute; keep the auto-stop timeout from restarting each time.
  const stopRef = useRef(onStop);
  stopRef.current = onStop;

  useEffect(() => startChime(), []);
  useEffect(() => {
    const t = setTimeout(() => stopRef.current(), AUTO_STOP_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div role="alertdialog" aria-modal="true" aria-label={`${sub} ${title}`} className="cover z-50 flex flex-col items-center justify-center" style={{ background: 'var(--ink)', color: 'var(--onInk)' }}>
      <div className="text-[18px] font-semibold uppercase tracking-eyebrow" style={{ color: 'var(--onInkSub)' }}>
        {sub}
      </div>
      <div className="tabular mt-4 font-semibold leading-none tracking-[-0.03em]" style={{ fontSize: 140 }}>
        {title}
      </div>
      <div className="mt-14 flex items-center">
        {snoozable ? (
          <OverlayButton onClick={snoozeRinging} outline>
            Snooze 9 min
          </OverlayButton>
        ) : (
          <OverlayButton onClick={() => extend(60_000)} outline>
            +1 min
          </OverlayButton>
        )}
        <span className="w-[28px]" />
        <OverlayButton onClick={onStop}>Stop</OverlayButton>
      </div>
    </div>
  );
}

function OverlayButton({ children, onClick, outline }: { children: string; onClick: () => void; outline?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="tap flex h-[88px] min-w-[220px] items-center justify-center rounded-full px-8 text-[22px] font-semibold"
      style={outline ? { boxShadow: 'inset 0 0 0 2px var(--onInkSub)', color: 'var(--onInk)' } : { background: 'var(--onInk)', color: 'var(--ink)' }}
    >
      {children}
    </button>
  );
}
